import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ContentPlatformDefinition,
  getContentPlatforms,
  getSyndicationCredentials,
  updateSyndicationCredentials,
} from '../../services/contentSyndicationService';

interface SyndicationCredentialsSettingsProps {
  onBack: () => void;
}

const categoryLabel: Record<ContentPlatformDefinition['category'], string> = {
  blog: 'Blog / Article Platforms',
  traffic: 'Traffic + Backlink Platforms',
  seo: 'SEO Platforms',
  custom: 'Custom Integrations',
};

const SyndicationCredentialsSettings: React.FC<SyndicationCredentialsSettingsProps> = ({ onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [platforms, setPlatforms] = useState<ContentPlatformDefinition[]>([]);
  const [credentials, setCredentials] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const groupedPlatforms = useMemo(() => {
    return platforms.reduce<Record<string, ContentPlatformDefinition[]>>((acc, platform) => {
      if (!acc[platform.category]) {
        acc[platform.category] = [];
      }
      acc[platform.category].push(platform);
      return acc;
    }, {});
  }, [platforms]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [platformData, credentialData] = await Promise.all([
          getContentPlatforms(),
          getSyndicationCredentials(),
        ]);
        setPlatforms(platformData);
        setCredentials(credentialData);
      } catch (loadError) {
        console.error('Failed to load syndication credential settings:', loadError);
        setError('Failed to load syndication credential settings. Please refresh.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleFieldChange = (platformId: string, fieldKey: string, value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [platformId]: {
        ...(prev[platformId] || {}),
        [fieldKey]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await updateSyndicationCredentials(credentials);
      setCredentials(saved);
      setMessage('Syndication credentials saved successfully.');
    } catch (saveError) {
      console.error('Failed to save syndication credentials:', saveError);
      setError('Failed to save syndication credentials. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const borderColor = theme.palette.divider;
  const cardShadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 4px 20px -5px rgba(0,0,0,0.05)';
  const mutedColor = theme.palette.text.secondary;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Syndication Credentials</Typography>
          <Typography variant="body2" color={mutedColor}>
            Yahan saare platform credentials add/update karo. Publish panel automatically in saved credentials ko use karega.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={onBack}>Back to Settings</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Credentials'}
          </Button>
        </Stack>
      </Box>

      {loading && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>
      )}

      {!loading && !error && (
        <Stack spacing={3}>
          {Object.entries(groupedPlatforms).map(([category, list]) => (
            <Card key={category} sx={{ borderRadius: 3, border: `1px solid ${borderColor}`, boxShadow: cardShadow }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  {categoryLabel[category as ContentPlatformDefinition['category']]}
                </Typography>

                <Stack spacing={2}>
                  {list.map((platform) => (
                    <Card
                      key={platform.id}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${borderColor}`,
                        boxShadow: 'none',
                        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.02),
                      }}
                    >
                      <CardContent>
                        <Box sx={{ mb: platform.requiredFields.length > 0 ? 2 : 0 }}>
                          <Typography variant="subtitle1" fontWeight={700}>{platform.name}</Typography>
                          <Typography variant="body2" color={mutedColor}>{platform.description}</Typography>
                          {platform.website && (
                            <Link href={platform.website} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>
                              {platform.website}
                            </Link>
                          )}
                        </Box>

                        {platform.requiredFields.length > 0 ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                            {platform.requiredFields.map((field) => (
                              <TextField
                                key={`${platform.id}-${field.key}`}
                                type={field.secret ? 'password' : field.type === 'url' ? 'url' : 'text'}
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                placeholder={field.placeholder}
                                helperText={field.helpText}
                                value={credentials[platform.id]?.[field.key] || ''}
                                onChange={(e) => handleFieldChange(platform.id, field.key, e.target.value)}
                                fullWidth
                                size="small"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color={mutedColor}>
                            Is platform ke liye public API-based credential configuration available nahi hai.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SyndicationCredentialsSettings;
